import { useCallback, useEffect, useState } from 'react';

import { cfetch } from '@confluence/network';

import { type SuggestionGroup } from '../types/RemoteObject.ts';

type SuggestionsResponse = {
	suggestions: SuggestionGroup[];
};

export type UseFetchSuggestionsResult = {
	groups: SuggestionGroup[];
	isLoading: boolean;
	refetch: () => Promise<void>;
};

export const useFetchSuggestions = (spaceKey?: string): UseFetchSuggestionsResult => {
	const [groups, setGroups] = useState<SuggestionGroup[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const refetch = useCallback(async () => {
		if (!spaceKey) {
			setGroups([]);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		try {
			const response = await cfetch(
				`/wiki/rest/internal/1.0/spaces/${spaceKey}/remote-objects/suggestions`,
				{ method: 'GET' },
			);

			const data: SuggestionsResponse = await response.json();
			setGroups(data.suggestions ?? []);
		} catch {
			// Silently fail — card won't render if there are no suggestions
		} finally {
			setIsLoading(false);
		}
	}, [spaceKey]);

	useEffect(() => {
		void refetch();
	}, [refetch]);

	return { groups, isLoading, refetch };
};
